import React, { useState } from 'react';
import { FieldOption } from '../../../interfaces/form-config.interface';
import Button from '../../../components/ui/Button';
import { DEFAULT_LOCALES } from './form-config.utils';

interface OptionsEditorProps {
  options: FieldOption[];
  onChange: (opts: FieldOption[]) => void;
}

export const OptionsEditor = ({ options, onChange }: OptionsEditorProps) => {
  const [extraLocales, setExtraLocales] = useState<string[]>([]);
  const [newLocale, setNewLocale] = useState('');

  const allLocales = Array.from(new Set([...DEFAULT_LOCALES, ...extraLocales]));

  const addOption = () =>
    onChange([...options, { value: '', label: '', translations: { en: '', 'zh-Hant': '' } }]);

  const removeOption = (idx: number) =>
    onChange(options.filter((_, i) => i !== idx));

  const setOptionValue = (idx: number, value: string) =>
    onChange(options.map((o, i) => i === idx ? { ...o, value } : o));

  const setOptionTranslation = (idx: number, locale: string, label: string) =>
    onChange(options.map((o, i) => {
      if (i !== idx) return o;
      const t = { ...(o.translations ?? {}), [locale]: label };
      return {
        ...o,
        translations: t,
        label: t['en'] ?? o.label,
      };
    }));

  const addLocale = () => {
    const code = newLocale.trim();
    if (!code || allLocales.includes(code)) return;
    setExtraLocales((p) => [...p, code]);
    setNewLocale('');
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Options
          <span className="ml-1 text-xs text-gray-400">({options.length})</span>
        </span>
        <div className="flex gap-1.5 items-center">
          {/* Add extra locale */}
          <div className="flex gap-1">
            <input
              type="text"
              className="w-20 border border-gray-200 dark:border-gray-600 rounded px-2 py-0.5 text-xs bg-white dark:bg-gray-800"
              placeholder="ja, ko…"
              value={newLocale}
              onChange={(e) => setNewLocale(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addLocale()}
            />
            <button
              type="button"
              onClick={addLocale}
              className="px-2 py-0.5 text-xs border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              + Locale
            </button>
          </div>
          <Button size="sm" variant="secondary" onClick={addOption}>+ Option</Button>
        </div>
      </div>

      {/* Header row */}
      {options.length > 0 && (
        <div className="grid text-xs font-medium text-gray-400 px-1" style={{ gridTemplateColumns: `5rem repeat(${allLocales.length}, 1fr) 1.5rem` }}>
          <span>Value</span>
          {allLocales.map((l) => <span key={l}>{l}</span>)}
          <span />
        </div>
      )}

      <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
        {options.map((opt, idx) => (
          <div key={idx} className="grid gap-1.5 items-center" style={{ gridTemplateColumns: `5rem repeat(${allLocales.length}, 1fr) 1.5rem` }}>
            <input
              className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              placeholder="value"
              value={opt.value}
              onChange={(e) => setOptionValue(idx, e.target.value)}
            />
            {allLocales.map((locale) => (
              <input
                key={locale}
                className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder={locale}
                value={opt.translations?.[locale] ?? (locale === 'en' ? opt.label : '')}
                onChange={(e) => setOptionTranslation(idx, locale, e.target.value)}
              />
            ))}
            <button
              type="button"
              onClick={() => removeOption(idx)}
              className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded text-xs"
            >✕</button>
          </div>
        ))}
      </div>
    </div>
  );
};
